// Bootstrap auth endpoints — called BEFORE the user has an access_token, so
// they bypass the openapi-fetch refresh interceptor. Each call sends
// `credentials: 'include'` so the BE-issued session_id cookie auto-attaches
// (CORS_ALLOW_CREDENTIALS=true on the BE is required for cross-origin dev).
import type { PersonaState } from "./store";

const baseUrl = import.meta.env.VITE_API_URL ?? "http://localhost:8080/api/v1";

async function call<T>(path: string, init: RequestInit): Promise<T> {
  const res = await fetch(`${baseUrl}${path}`, {
    ...init,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init.headers ?? {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = (body as { error?: string }).error ?? `HTTP ${res.status}`;
    const code = (body as { code?: string }).code;
    throw Object.assign(new Error(msg), { status: res.status, code });
  }
  return body as T;
}

// AuthError carries the BE-supplied `code` field so callers can branch on
// reason (pin_invalid / session_expired / upstream_refresh_failed / …).
export type AuthError = Error & { status?: number; code?: string };

export type EnrollInput = {
  access_token: string;
  refresh_token: string;
  refresh_expires_in?: number;
  fingerprint: string;
  pin: string;
  label?: string;
  user_agent?: string;
};
export type StaffField = {
  id: string;
  name: string;
  role: string;
  department: string;
  /** Mặc định có mặt cả tuần, chỉ khai ngày nghỉ. Xem `Staff` ở `store.ts`. */
  skipsWeeklyShift?: boolean;
};

export type BranchScopeField = {
  source: "database" | "shadow" | "casdoor";
  scopeCodes: string[];
  branches: string[];
  primaryBranch: string;
};

/**
 * QĐ 0132: máy chủ báo người này có được nhận tiền đơn / liên lạc khách không.
 * Hợp đồng OpenAPI ghi bắt buộc; máy chủ cũ chưa có trường này thì kho phiên
 * giữ cờ đang có (`setSession`), không mở lại ô tiền.
 */
export type DataVisibilityField = {
  orderMoney: boolean;
  customerContact: boolean;
};

export type EnrollOutput = {
  access_token: string;
  casdoor_sub: string;
  display_name: string;
  staff: StaffField | null;
  permissions?: string[];
  branchScope: BranchScopeField;
  dataVisibility: DataVisibilityField;
  /** QĐ 0255: danh phận đang dùng trên máy này + danh sách để đổi. */
  persona?: PersonaState | null;
};
export const enroll = (i: EnrollInput) =>
  call<EnrollOutput>("/auth/enroll", { method: "POST", body: JSON.stringify(i) });

export type PinLoginInput = { fingerprint: string; casdoor_sub: string; pin: string };
export type PinLoginOutput = {
  access_token?: string;
  expires_in: number;
  casdoor_sub: string;
  display_name: string;
  staff: StaffField | null;
  permissions?: string[];
  branchScope: BranchScopeField;
  dataVisibility: DataVisibilityField;
  /** QĐ 0255: danh phận đang dùng trên máy này + danh sách để đổi. */
  persona?: PersonaState | null;
};
export const pinLogin = (i: PinLoginInput) =>
  call<PinLoginOutput>("/auth/pin-login", { method: "POST", body: JSON.stringify(i) });

export type RefreshOutput = {
  access_token?: string;
  expires_in: number;
  casdoor_sub: string;
  display_name: string;
  staff: StaffField | null;
  permissions?: string[];
  branchScope: BranchScopeField;
  dataVisibility: DataVisibilityField;
  /** QĐ 0255: danh phận đang dùng trên máy này + danh sách để đổi. */
  persona?: PersonaState | null;
};
export const refresh = () =>
  call<RefreshOutput>("/auth/refresh", { method: "POST" });

export const logout = () =>
  call<{ ok: boolean }>("/auth/logout", { method: "POST" });

export type ChangePasswordInput = {
  current_password: string;
  new_password: string;
};
export const changePassword = (i: ChangePasswordInput) =>
  call<{ ok: boolean }>("/auth/change-password", { method: "POST", body: JSON.stringify(i) });

export type AccountListing = {
  casdoor_sub: string;
  display_name: string;
  avatar_url?: string;
  last_login_at?: string;
};
export const listAccounts = (fingerprint: string) =>
  call<{ accounts: AccountListing[] }>(`/auth/device/${encodeURIComponent(fingerprint)}/accounts`, {
    method: "GET",
  }).then((r) => r.accounts ?? []);

export type StaffLinkSyncOutput = {
  status: "linked" | "already_linked";
  staff: StaffField;
};
// The bearer token is optional: under cookie-session the store holds no
// Casdoor token after a PIN login (the BE snapshot fast path returns none),
// and the session_id cookie sent by `call` identifies the caller on its own.
// Requiring a token here made the staff-link screen unreachable for exactly
// the accounts that need it.
export const syncStaffLink = (accessToken?: string | null) =>
  call<StaffLinkSyncOutput>("/auth/sync-staff-link", {
    method: "POST",
    ...(accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : {}),
  });
